// Wire shapes for the doctor portal.
//
// Mirrors homecare.models.doctor_portal. Hand-written for the same reason the
// api client is: these endpoints are not on dev yet, so the generator cannot see
// them. Replace with the generated types after the backend deploys — the field
// names here are the camelCase the API actually emits, so the swap is a rename
// of the import and nothing else.

export type VitalMetric =
  | 'BLOOD_PRESSURE_SYSTOLIC'
  | 'BLOOD_PRESSURE_DIASTOLIC'
  | 'HEART_RATE'
  | 'BLOOD_GLUCOSE'
  | 'TEMPERATURE';

export type Paginated<T> = {
  total: number;
  offset: number;
  limit: number;
  data: T[];
};

export type MyDoctorRecord = {
  doctorId: number;
  firstName: string;
  lastName: string;
  email: string;
  specialization?: string | null;
};

export type DoctorPatient = {
  clientId: number;
  firstName: string;
  lastName: string;
  /** Comma-separated when the doctor holds more than one link to the patient. */
  doctorType: string;
  startDate: string;
  /**
   * The latest end date across this client's links to this doctor, and only once
   * every one of them is closed. Null while any link is still open, so it is not
   * the inverse of `isActive`: a patient with one ended and one open link sends
   * null here and `isActive: true`.
   */
  endDate?: string | null;
  /**
   * True when at least one link to this doctor is still open. The row is a
   * CLIENT, not a link, so this is not `endDate == null`.
   *
   * Optional because the field does not exist until the backend deploys, and
   * absent reads as active: the endpoint returns the active roster unless
   * `includeEnded` is asked for, so every row of an unwidened list really is
   * active. Read it as `isActive === false` for "ended".
   */
  isActive?: boolean;
  lastVisitAt?: string | null;
  nextVisitAt?: string | null;
  completedVisits: number;
  scheduledVisits: number;
};

export type DoctorThreshold = {
  thresholdId?: number | null;
  doctorId: number;
  metric: VitalMetric;
  lowValue?: string | number | null;
  highValue?: string | number | null;
  isEnabled: boolean;
  /** True when no row of the doctor's own exists and the clinical default is reported. */
  isDefault: boolean;
};

export type DoctorThresholdUpsert = {
  metric: VitalMetric;
  lowValue?: number | null;
  highValue?: number | null;
  isEnabled: boolean;
};

export type ThresholdBreach = {
  visitId: number;
  clientId: number;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  metric: VitalMetric;
  value: string | number;
  limitValue: string | number;
  side: 'high' | 'low';
  measuredAt: string;
};

/** Unit per metric, for display. The API sends bare numbers. */
export const METRIC_UNITS: Record<VitalMetric, string> = {
  BLOOD_PRESSURE_SYSTOLIC: 'mmHg',
  BLOOD_PRESSURE_DIASTOLIC: 'mmHg',
  HEART_RATE: 'bpm',
  BLOOD_GLUCOSE: 'mg/dL',
  TEMPERATURE: '°C',
};

/** i18n key per metric — never a hardcoded label for an enum value. */
export const METRIC_LABEL_KEYS: Record<VitalMetric, string> = {
  BLOOD_PRESSURE_SYSTOLIC: 'doctor-metric-systolic',
  BLOOD_PRESSURE_DIASTOLIC: 'doctor-metric-diastolic',
  HEART_RATE: 'doctor-metric-heart-rate',
  BLOOD_GLUCOSE: 'doctor-metric-glucose',
  TEMPERATURE: 'doctor-metric-temperature',
};

// ── Comments and instructions ────────────────────────────────────────────────────────

export type DoctorCommentVisibility = 'CARE_TEAM' | 'MEDICAL_ONLY';

export type DoctorComment = {
  commentId: number;
  visitId: number;
  doctorId: number;
  body: string;
  visibility: DoctorCommentVisibility;
  createdAt: string;
  updatedAt?: string | null;
  doctorName?: string | null;
};

// No `visibility`: the server fixes the audience to the office and the patient's
// other doctors, and has no field for it. `DoctorComment` above keeps the word,
// because comments stored before that rule really are CARE_TEAM and read back so.
export type DoctorCommentCreate = {
  body: string;
};

export type DoctorInstructionStatus = 'PROCESSING' | 'HANDLED' | 'NOT_HANDLED';
export type DoctorInstructionPriority = 'LOW' | 'NORMAL' | 'HIGH';

export type DoctorInstruction = {
  instructionId: number;
  visitId: number;
  /**
   * The patient, joined through the visit. Present when the instruction came from a LIST;
   * absent on the row returned straight from a create or an edit, whose query is
   * the instruction table alone. Read it as "unknown here", never as "no patient".
   */
  clientId?: number | null;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  doctorId: number;
  serviceId?: number | null;
  title: string;
  details?: string | null;
  priority: DoctorInstructionPriority;
  status: DoctorInstructionStatus;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote?: string | null;
  createdAt: string;
};

export type DoctorInstructionCreate = {
  visitId: number;
  title: string;
  serviceId?: number | null;
  details?: string | null;
  priority: DoctorInstructionPriority;
};
